import { useEffect, useState, useCallback } from "react";
import { api, currency, dateShort, fileUrl } from "../lib/api";
import { PageHeader, EmptyState } from "../components/Bits";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Plus, Upload, Trash2, CheckCircle2, ArrowRight, XCircle, ClipboardList } from "lucide-react";
import { toast } from "sonner";

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [stages, setStages] = useState([]);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [customerId, setCustomerId] = useState("");
  const [items, setItems] = useState([{ product_id: "", qty: 1 }]);
  const [notes, setNotes] = useState("");
  const [source, setSource] = useState("etsy");
  const [file, setFile] = useState(null);
  const [filter, setFilter] = useState("active");

  const load = useCallback(() => {
    const params = filter === "all" ? {} : { status: filter };
    api.get("/orders", { params }).then((r) => setOrders(r.data));
  }, [filter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/stages").then((r) => setStages(r.data));
  }, []);

  const addItem = () => setItems([...items, { product_id: "", qty: 1 }]);
  const rmItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const setItem = (i, k, v) => setItems(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));

  const submit = async () => {
    if (!customerId || items.some((it) => !it.product_id)) return toast.error("Müşteri ve ürün seçin");
    try {
      await api.post("/orders", { customer_id: customerId, items: items.map((it) => ({ product_id: it.product_id, qty: Number(it.qty) })), notes, source });
      toast.success("Sipariş oluşturuldu");
      setOpen(false); setCustomerId(""); setItems([{ product_id: "", qty: 1 }]); setNotes(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  const advance = async (o) => {
    const idx = stages.findIndex((s) => s.id === o.current_stage_id);
    if (idx < 0 || idx >= stages.length - 1) return;
    await api.post(`/orders/${o.id}/move`, { stage_id: stages[idx + 1].id });
    load();
  };
  const complete = async (o) => {
    try {
      const { data } = await api.post(`/orders/${o.id}/complete`);
      const cost = Number(data?.production_cost || 0);
      const profit = Number(data?.profit || 0);
      const short = data?.shortages || [];
      toast.success(`Tamamlandı · Maliyet $${cost.toFixed(2)} · Kâr $${profit.toFixed(2)}`);
      if (short.length) toast.warning(`Stok yetersiz: ${short.map((s) => s.name).join(", ")}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hata");
    }
  };
  const cancel = async (o) => { await api.post(`/orders/${o.id}/cancel`); toast.success("İptal edildi"); load(); };
  const del = async (o) => { if (!window.confirm("Silinsin mi?")) return; await api.delete(`/orders/${o.id}`); load(); };

  const doImport = async () => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/orders/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.imported} sipariş içe aktarıldı`);
      setImportOpen(false); setFile(null); load();
    } catch (e) { toast.error("İçe aktarım hatası"); }
  };

  return (
    <div className="p-8">
      <PageHeader
        eyebrow="Satış"
        title="Siparişler"
        subtitle="Manuel giriş veya CSV içe aktarma"
        actions={
          <div className="flex gap-2">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-44" data-testid="orders-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Aktif</SelectItem>
                <SelectItem value="completed">Tamamlanan</SelectItem>
                <SelectItem value="cancelled">İptal Edilen</SelectItem>
                <SelectItem value="all">Tümü</SelectItem>
              </SelectContent>
            </Select>
            <Dialog open={importOpen} onOpenChange={setImportOpen}>
              <DialogTrigger asChild>
                <Button data-testid="orders-import-btn" variant="outline"><Upload className="h-4 w-4 mr-2" />CSV İçe Aktar</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Siparişleri CSV&apos;den içe aktar</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div className="text-xs text-slate-500">Beklenen sütunlar: <span className="font-mono">customer_name, product_name, qty, price, notes, source</span></div>
                  <Input data-testid="orders-import-file" type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0])} />
                </div>
                <DialogFooter><Button data-testid="orders-import-submit" onClick={doImport} className="bg-blue-600 hover:bg-blue-700">Yükle</Button></DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="orders-new-btn" className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-2" />Yeni Sipariş</Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader><DialogTitle>Yeni Sipariş</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Müşteri</Label>
                    <Select value={customerId} onValueChange={setCustomerId}>
                      <SelectTrigger data-testid="order-customer-select"><SelectValue placeholder="Müşteri seçin" /></SelectTrigger>
                      <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Ürünler</Label>
                    {items.map((it, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <Select value={it.product_id} onValueChange={(v) => setItem(i, "product_id", v)}>
                          <SelectTrigger className="flex-1"><SelectValue placeholder="Ürün seçin" /></SelectTrigger>
                          <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {currency(p.price)}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input type="number" min={1} value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} className="w-24" />
                        <Button variant="ghost" size="icon" onClick={() => rmItem(i)}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    ))}
                    <Button variant="outline" onClick={addItem} size="sm"><Plus className="h-3 w-3 mr-1" />Ürün ekle</Button>
                  </div>
                  <div>
                    <Label>Kaynak</Label>
                    <Select value={source} onValueChange={setSource}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="etsy">Etsy</SelectItem>
                        <SelectItem value="manual">Manuel</SelectItem>
                        <SelectItem value="trendyol">Trendyol</SelectItem>
                        <SelectItem value="hepsiburada">Hepsiburada</SelectItem>
                        <SelectItem value="shopify">Shopify</SelectItem>
                        <SelectItem value="whatsapp">WhatsApp</SelectItem>
                        <SelectItem value="diger">Diğer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Notlar</Label>
                    <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Sipariş notu..." />
                  </div>
                </div>
                <DialogFooter><Button data-testid="order-submit-btn" onClick={submit} className="bg-blue-600 hover:bg-blue-700">Kaydet</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      {orders.length === 0 ? (
        <EmptyState icon={ClipboardList} title="Henüz sipariş yok" description="İlk siparişinizi oluşturun ya da CSV içe aktarın." />
      ) : (
        <Card className="border-slate-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase tracking-widest">Sipariş No</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Müşteri</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Ürünler</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Aşama</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Tutar</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Tarih</TableHead>
                <TableHead className="text-xs uppercase tracking-widest text-right">İşlem</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((o) => (
                <TableRow key={o.id} className="hover:bg-slate-50">
                  <TableCell className="font-mono text-xs font-semibold">{o.order_no}</TableCell>
                  <TableCell className="font-medium">{o.customer?.name || "-"}</TableCell>
                  <TableCell className="text-sm text-slate-600">
                    <div className="flex items-center gap-2 flex-wrap">
                      {o.items?.slice(0, 3).map((i, idx) => (
                        <div key={`${o.id}-${idx}`} className="flex items-center gap-1.5">
                          {i.image_url ? (
                            <img src={i.image_url} alt="" loading="lazy" className="h-7 w-7 rounded object-cover border border-slate-200" />
                          ) : null}
                          <span className="text-xs">{i.qty}× {i.product_name}</span>
                        </div>
                      ))}
                      {o.items && o.items.length > 3 && <span className="text-xs text-slate-400">+{o.items.length - 3}</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    {o.status === "completed" ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Tamamlandı</Badge>
                      : o.status === "cancelled" ? <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200">İptal</Badge>
                      : <Badge style={{ backgroundColor: `${o.stage?.color}20`, color: o.stage?.color, borderColor: `${o.stage?.color}40` }} className="border">{o.stage?.name || "-"}</Badge>}
                  </TableCell>
                  <TableCell className="font-semibold">{currency(o.total)}</TableCell>
                  <TableCell className="text-sm text-slate-500">{dateShort(o.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {o.status === "active" && (
                        <>
                          <Button size="icon" variant="ghost" title="Sonraki aşama" onClick={() => advance(o)}><ArrowRight className="h-4 w-4" /></Button>
                          <Button size="icon" variant="ghost" title="Tamamla" onClick={() => complete(o)}><CheckCircle2 className="h-4 w-4 text-emerald-600" /></Button>
                          <Button size="icon" variant="ghost" title="İptal" onClick={() => cancel(o)}><XCircle className="h-4 w-4 text-slate-500" /></Button>
                        </>
                      )}
                      <Button size="icon" variant="ghost" onClick={() => del(o)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
